// app/privacy/page.js
'use client'

import { useI18n } from '@/components/i18n'
import { useRef, useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { SiteSocialLinksRow } from '../../components/TopBar'
import FooterViewportPark from '../../components/FooterViewportPark'

const POLICY_VERSION = '2026-09-28'

function PageMarqueeTail() {
  const { t } = useI18n()
  const marqueeRef = useRef(null)

  useEffect(() => {
    const el = marqueeRef.current
    if (!el) return
    if (el.dataset.duped === '1') return
    el.innerHTML += el.innerHTML
    el.dataset.duped = '1'
  }, [])

  return (
    <section className="marquee-wrap no-gutters" aria-hidden="true" data-ql7-visual-scope="panel">
      <div className="marquee" ref={marqueeRef}>
        <span>{t('marquee')}</span>
        <span>{t('marquee')}</span>
        <span>{t('marquee')}</span>
        <span>{t('marquee')}</span>
      </div>
    </section>
  )
}

function textValue(t, key, fallback) {
  const value = t(key)
  return typeof value === 'string' && value.trim() && value !== key ? value : fallback
}

function arrayValue(t, key) {
  const value = t(key)
  return Array.isArray(value) ? value : []
}

function sectionId(index) {
  return `privacy-chapter-${String(index + 1).padStart(2, '0')}`
}

function PolicyMap({ t, sections }) {
  const [mapOpen, setMapOpen] = useState(false)
  const mapRef = useRef(null)

  useEffect(() => {
    if (!mapOpen) return undefined

    const closeOnOutsidePointer = (event) => {
      if (!mapRef.current?.contains(event.target)) setMapOpen(false)
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMapOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer, true)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [mapOpen])

  return (
    <nav
      className="privacy-map"
      aria-label={textValue(t, 'privacy_toc_label', 'Policy map')}
      data-open={mapOpen ? 'true' : 'false'}
      ref={mapRef}
    >
      <button
        type="button"
        className="privacy-map-trigger"
        aria-expanded={mapOpen}
        aria-haspopup="true"
        aria-controls="privacy-map-popover"
        onClick={() => setMapOpen((open) => !open)}
      >
        <span className="privacy-map-trigger-glyph" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="privacy-map-heading">
          <strong>{textValue(t, 'privacy_toc_label', 'Policy map')}</strong>
          <span>{textValue(t, 'privacy_expand_hint', 'Select a chapter or expand it below')}</span>
        </span>
        <span className="privacy-map-total" aria-hidden="true">{sections.length}</span>
        <span className="privacy-map-chevron" aria-hidden="true">⌄</span>
      </button>

      {mapOpen ? (
        <div
          id="privacy-map-popover"
          className="privacy-map-popover"
          aria-label={textValue(t, 'privacy_toc_label', 'Policy map')}
        >
          <div className="privacy-map-popover-rail" aria-hidden="true">
            <span />
            <i />
            <span />
          </div>
          <div className="privacy-map-list">
            {sections.map((section, index) => {
              const id = sectionId(index)
              const ruleCount = (section?.paras?.length || 0) + (section?.list?.length || 0)
              return (
                <a
                  className="privacy-map-option"
                  href={`#${id}`}
                  key={id}
                  onClick={() => setMapOpen(false)}
                >
                  <span className="privacy-map-option-number">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="privacy-map-option-copy">
                    <small>{textValue(t, 'privacy_chapter_label', 'Chapter')}</small>
                    <strong>{section?.title || `${index + 1}`}</strong>
                  </span>
                  <span className="privacy-map-option-count">
                    {ruleCount} {textValue(t, 'privacy_points_short_label', 'rules')}
                  </span>
                </a>
              )
            })}
          </div>
        </div>
      ) : null}
    </nav>
  )
}

export default function PrivacyPage() {
  const { t, lang } = useI18n()
  const direction = lang === 'ar' ? 'rtl' : 'ltr'
  const h1 = textValue(t, 'privacy_title', 'Privacy Policy')
  const updatedLabel = textValue(t, 'privacy_updated_label', 'Updated:')
  const updated = textValue(t, 'privacy_updated', POLICY_VERSION)
  const sections = arrayValue(t, 'privacy_sections')
  const jurisdictions = arrayValue(t, 'privacy_jurisdictions')
  const totalRules = sections.reduce(
    (total, section) => total + (section?.paras?.length || 0) + (section?.list?.length || 0),
    0,
  )

  return (
    <>
      <div className="ql7-editorial-unity-canvas" data-ql7-editorial-unity="privacy">
      <main
        className="privacy-page"
        lang={lang}
        dir={direction}
        data-ql7-visual-scope="route"
        data-ql7-privacy-policy={POLICY_VERSION}
      >
        <article className="privacy-shell">
          <header className="privacy-hero" data-ql7-visual-scope="panel">
            <div className="privacy-hero-copy">
              <p className="privacy-kicker">{textValue(t, 'privacy_kicker', 'GLOBAL PRIVACY STANDARD · 2026')}</p>
              <h1>{h1}</h1>
              <p className="privacy-lead">
                {textValue(
                  t,
                  'privacy_lead',
                  'A transparent account of how Quantum L7 AI handles data, protects users, and applies mandatory local privacy law.',
                )}
              </p>
            </div>

            <dl className="privacy-facts" aria-label={textValue(t, 'privacy_facts_label', 'Policy facts')}>
              <div>
                <dt>{updatedLabel}</dt>
                <dd><time dateTime={POLICY_VERSION}>{updated}</time></dd>
              </div>
              <div>
                <dt>{textValue(t, 'privacy_scope_label', 'Coverage')}</dt>
                <dd>{textValue(t, 'privacy_scope_value', 'Global baseline + mandatory local law')}</dd>
              </div>
              <div>
                <dt>{textValue(t, 'privacy_sections_label', 'Legal chapters')}</dt>
                <dd>{sections.length}</dd>
              </div>
              <div>
                <dt>{textValue(t, 'privacy_rules_label', 'Documented rules')}</dt>
                <dd>{totalRules}</dd>
              </div>
            </dl>
          </header>

          <section className="privacy-legal-landscape" aria-labelledby="privacy-law-notice-title">
            <header className="privacy-law-notice">
              <div className="privacy-law-notice-mark" aria-hidden="true">§</div>
              <div>
                <h2 id="privacy-law-notice-title">
                  {textValue(t, 'privacy_jurisdiction_title', 'How applicable law is determined')}
                </h2>
                <p>
                  {textValue(
                    t,
                    'privacy_jurisdiction_intro',
                    'This policy is a global baseline. The law that applies depends on the user, the processing activity, the operator role, and mandatory territorial or sector rules.',
                  )}
                </p>
              </div>
            </header>

            {jurisdictions.length > 0 ? (
              <div className="privacy-jurisdictions" aria-label={textValue(t, 'privacy_jurisdiction_grid_label', 'Regional privacy frameworks')}>
                {jurisdictions.map((item, index) => (
                  <article className="privacy-jurisdiction-card" key={`${item?.code || 'region'}-${index}`}>
                    <span>{item?.code || String(index + 1).padStart(2, '0')}</span>
                    <h3>{item?.title}</h3>
                    <p>{item?.body}</p>
                  </article>
                ))}
              </div>
            ) : null}
          </section>

          {sections.length > 0 ? (
            <>
              <PolicyMap t={t} sections={sections} />

              <section className="privacy-chapters" aria-label={textValue(t, 'privacy_sections_label', 'Legal chapters')}>
                {sections.map((section, sectionIndex) => {
                  const id = sectionId(sectionIndex)
                  const rules = [
                    ...(Array.isArray(section?.paras) ? section.paras : []),
                    ...(Array.isArray(section?.list) ? section.list : []),
                  ]
                  const details = Array.isArray(section?.details) ? section.details : []

                  return (
                    <section
                      className="privacy-chapter"
                      id={id}
                      key={id}
                      data-privacy-detail-count={details.length}
                    >
                      <header className="privacy-chapter-header">
                        <span className="privacy-chapter-rail" aria-hidden="true">
                          {String(sectionIndex + 1).padStart(2, '0')}
                        </span>
                        <span className="privacy-chapter-title">
                          <span>{textValue(t, 'privacy_chapter_label', 'Chapter')}</span>
                          <strong>{section?.title || `${sectionIndex + 1}`}</strong>
                        </span>
                        <span className="privacy-chapter-count">
                          {rules.length} {textValue(t, 'privacy_points_short_label', 'rules')}
                        </span>
                      </header>

                      <div className="privacy-chapter-body">
                        <div className="privacy-rule-grid">
                          {rules.map((rule, ruleIndex) => (
                            <article className="privacy-rule" key={`${id}-rule-${ruleIndex}`}>
                              <span className="privacy-rule-index">
                                {textValue(t, 'privacy_point_label', 'Policy rule')} {String(ruleIndex + 1).padStart(2, '0')}
                              </span>
                              <p>{rule}</p>
                            </article>
                          ))}
                        </div>

                        {details.length > 0 ? (
                          <section className="privacy-expansion" aria-label={textValue(t, 'privacy_legal_details_label', 'Expanded legal detail')}>
                            <header>
                              <span>{textValue(t, 'privacy_legal_details_kicker', 'FIVE-LAYER LEGAL REVIEW')}</span>
                              <h3>{textValue(t, 'privacy_legal_details_label', 'Expanded legal detail')}</h3>
                              <p>{textValue(t, 'privacy_legal_details_intro', 'Each chapter is interpreted through five distinct legal and operational controls.')}</p>
                            </header>
                            <div className="privacy-detail-grid">
                              {details.map((detail, detailIndex) => (
                                <details className="privacy-legal-detail" key={`${id}-detail-${detailIndex}`}>
                                  <summary>
                                    <span>{String(detailIndex + 1).padStart(2, '0')}</span>
                                    <strong>{detail?.title}</strong>
                                    <i aria-hidden="true">+</i>
                                  </summary>
                                  <p>{detail?.body}</p>
                                </details>
                              ))}
                            </div>
                          </section>
                        ) : null}
                      </div>
                    </section>
                  )
                })}
              </section>
            </>
          ) : (
            <section className="privacy-empty" data-ql7-visual-scope="panel">
              <p>{textValue(t, 'privacy_empty', 'The policy is temporarily unavailable.')}</p>
            </section>
          )}

          <section className="privacy-rights-callout" aria-labelledby="privacy-rights-callout-title">
            <div>
              <span>{textValue(t, 'privacy_contact_kicker', 'CONTROL YOUR DATA')}</span>
              <h2 id="privacy-rights-callout-title">{textValue(t, 'privacy_contact_title', 'Exercise your privacy rights')}</h2>
              <p>{textValue(t, 'privacy_contact_body', 'Requests are verified proportionately and answered within the period required by the law that applies to you.')}</p>
            </div>
            <div className="privacy-contact-actions">
              <a href="mailto:quantuml7ai@gmail.com">{textValue(t, 'privacy_email_label', 'Privacy email')}</a>
              <a href="https://t.me/L7ai_feedback" target="_blank" rel="noreferrer">
                {textValue(t, 'privacy_bot_label', 'Feedback bot')}
              </a>
            </div>
          </section>
        </article>
      </main>
      </div>

<div className="wrap">
      {/* Хвост страницы: бегущая строка во всю ширину */}
      <FooterViewportPark scope="privacy-footer-marquee" reserveMarginTopPx={10}>
        <PageMarqueeTail />
      </FooterViewportPark>
</div>      
      {/* ===== ИКОНКИ ПОСЛЕ МАРКИЗЫ (глобальные стили ql7-*, как на главной/subscribe) ===== */}
      <FooterViewportPark scope="privacy-footer-actions">
      <div className="ql7-icons-row ql7-footer-social-layout" data-ql7-visual-scope="panel">
        <Link
          href="/privacy"
          className="ql7-icon-link ql7-footer-policy-link"
          aria-label="Privacy / Политика"
          style={{ '--ql7-icon-size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/policy.png"
            alt="Privacy"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>

       <SiteSocialLinksRow className="ql7-social-links-row--footer" />

        <Link
          href="/forum?ql7SupportOpen=1&inbox=messages&dmUser=ql7-support"
          className="ql7-icon-link ql7-footer-support-link"
          data-ql7-support-entry="1"
          aria-label="Support / Поддержка"
          style={{ '--ql7-icon-size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/support.png"
            alt="Support"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>
      </div>
      </FooterViewportPark>
    </>
  )
}
